import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getIconMethodPaymentSimple } from "@/utils/getIconMethodPayment";
import { TFunction } from "i18next";
import React, { useMemo } from "react";
import { CircleFlag } from "react-circle-flags";
import { Globe } from "lucide-react";
import {
  getAlpha2,
  getCountryName,
  getRealCountries,
  isGlobalMethod,
} from "@/components/shared/PaymentMethodSelector/country-registry";
import { OfferData } from "@/@types/Products/Offers";
import { useAuth } from "@/auth/Auth";

interface PaymentMethodsIconsProps {
  offer: OfferData;
  t: TFunction;
}

interface PaymentMethodEntry {
  type: string;
  name: string;
  countries?: string[];
}

interface GroupedMethods {
  countryCode: string;
  name: string;
  alpha2: string;
  methods: PaymentMethodEntry[];
}

function groupMethodsByCountry(methods: PaymentMethodEntry[]): GroupedMethods[] {
  const groupsMap = new Map<string, PaymentMethodEntry[]>();

  for (const method of methods) {
    const countryCodes = method.countries || [];

    if (countryCodes.length === 0 || isGlobalMethod(countryCodes)) {
      const existing = groupsMap.get("GLOBAL") || [];
      existing.push(method);
      groupsMap.set("GLOBAL", existing);
    } else {
      const realCountries = getRealCountries(countryCodes);
      if (realCountries.length === 0) {
        const existing = groupsMap.get("GLOBAL") || [];
        existing.push(method);
        groupsMap.set("GLOBAL", existing);
      } else {
        for (const code of realCountries) {
          const existing = groupsMap.get(code) || [];
          if (!existing.some((m) => m.type === method.type)) {
            existing.push(method);
            groupsMap.set(code, existing);
          }
        }
      }
    }
  }

  const groups: GroupedMethods[] = [];
  for (const [code, groupMethods] of Array.from(groupsMap.entries())) {
    if (code === "GLOBAL") {
      groups.push({ countryCode: "GLOBAL", name: "Global", alpha2: "", methods: groupMethods });
    } else {
      groups.push({
        countryCode: code,
        name: getCountryName(code),
        alpha2: getAlpha2(code),
        methods: groupMethods,
      });
    }
  }

  groups.sort((a, b) => {
    if (a.countryCode === "GLOBAL") return -1;
    if (b.countryCode === "GLOBAL") return 1;
    return a.name.localeCompare(b.name);
  });

  return groups;
}

const MAX_VISIBLE = 5;

export function PaymentMethodsIcons({ offer, t }: PaymentMethodsIconsProps) {
  const { authData } = useAuth();

  const countryLookup = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const method of authData?.available_payment_methods ?? []) {
      map.set(method.type, method.countries);
    }
    return map;
  }, [authData?.available_payment_methods]);

  const activeMethods: PaymentMethodEntry[] = useMemo(
    () =>
      (offer.paymentMethods || []).map((method) => ({
        ...method,
        countries:
          method.countries?.length
            ? method.countries
            : countryLookup.get(method.type) ?? [],
      })),
    [offer.paymentMethods, countryLookup],
  );
  const uniqueMethods = Array.from(
    new Map(activeMethods.map((pm) => [pm.type, pm])).values()
  );

  const visibleMethods = uniqueMethods.slice(0, MAX_VISIBLE);
  const hiddenCount = uniqueMethods.length - MAX_VISIBLE;
  const allGroups = groupMethodsByCountry(uniqueMethods);

  return (
    <div className="flex items-center gap-1.5">
      {visibleMethods.map(({ type }) => {
        const icon = getIconMethodPaymentSimple(type);
        if (!icon) return null;
        return (
          <div
            key={type}
            className="w-6 h-6 flex items-center justify-center rounded bg-zinc-800/60 p-0.5 shrink-0"
            title={type.replace(/_/g, " ")}
          >
            {React.cloneElement(icon, {
              width: 18,
              height: 18,
            })}
          </div>
        );
      })}

      {hiddenCount > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="cursor-pointer rounded-md bg-zinc-800 px-2 py-1 text-[11px] font-semibold text-zinc-300 hover:bg-zinc-700 hover:text-white transition-all duration-150 active:scale-95 shrink-0"
            >
              +{hiddenCount}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="start"
            className="w-[320px] max-h-[380px] overflow-y-auto p-0 bg-zinc-900 border-zinc-700/80 shadow-2xl rounded-xl"
          >
            <div className="px-4 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <h4 className="text-sm font-semibold text-white">
                {t("common:payment_methods")}
              </h4>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {uniqueMethods.length} {t("common:configured")}
              </p>
            </div>

            <div className="p-2 space-y-1">
              {allGroups.map((group) => (
                <div key={group.countryCode}>
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      {group.countryCode === "GLOBAL" ? (
                        <Globe size={14} className="text-zinc-500" />
                      ) : (
                        <div className="w-[14px] h-[14px] rounded-full overflow-hidden">
                          <CircleFlag
                            countryCode={group.alpha2}
                            height={14}
                            width={14}
                          />
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">
                      {group.name}
                    </span>
                  </div>

                  <div className="space-y-0.5">
                    {group.methods.map((method) => {
                      const icon = getIconMethodPaymentSimple(method.type);
                      return (
                        <div
                          key={method.type}
                          className="flex items-center gap-2.5 px-3 py-1.5 rounded-md hover:bg-zinc-800/60 transition-colors"
                        >
                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                            {icon &&
                              React.cloneElement(icon, {
                                width: 18,
                                height: 18,
                              })}
                          </div>
                          <span className="text-xs text-zinc-300 truncate">
                            {method.name ||
                              method.type
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {uniqueMethods.length === 0 && (
        <span className="text-xs text-zinc-500">{t("common:none")}</span>
      )}
    </div>
  );
}
